/* ==========================================================================
   Fit Quiz
   Lazy init on first trigger click. State, step navigation, size calculation,
   variant lookup, /cart/add.js, localStorage caching, focus trap, ESC.
   ========================================================================== */
(function () {
  'use strict';

  const SIZES = ['3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
  const LS_PREFIX = 'fit_quiz_result_';

  /* ---------- Init per root ---------- */
  function initRoot(root) {
    if (root.dataset.fqInitialized === 'true') return;
    root.dataset.fqInitialized = 'true';

    const trigger = root.querySelector('[data-fq-trigger]');
    const dataEl = root.querySelector('[data-fq-variants]');
    const hint = root.querySelector('[data-fq-cached-hint]');
    if (!trigger || !dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[fit-quiz] invalid variant JSON', e);
      return;
    }

    showCachedHint(hint, data.productId);

    let modalState = null;

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (!modalState) modalState = mountModal(root, data);
      openModal(modalState);
    });
  }

  function showCachedHint(hint, productId) {
    if (!hint) return;
    const cached = readCache(productId);
    if (!cached) return;
    hint.innerHTML = ' · empfohlen: <strong>' + escapeHtml(cached) + '</strong>';
    hint.hidden = false;
  }

  function readCache(productId) {
    try {
      return localStorage.getItem(LS_PREFIX + productId) || null;
    } catch (e) {
      return null;
    }
  }

  function writeCache(productId, size) {
    try {
      localStorage.setItem(LS_PREFIX + productId, size);
    } catch (e) {
      /* noop */
    }
  }

  /* ---------- Modal mount (portal to body) ---------- */
  function mountModal(root, data) {
    const tpl = root.querySelector('[data-fq-template]');
    if (!tpl) return null;

    const overlay = tpl.content.querySelector('[data-fq-overlay]').cloneNode(true);
    const modal = tpl.content.querySelector('[data-fq-modal]').cloneNode(true);

    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    const state = {
      root: root,
      data: data,
      overlay: overlay,
      modal: modal,
      currentStep: 1,
      answers: {
        gender: null,
        currentSize: null,
        consistency: null,
        belly: null,
        body: null,
        heightCm: 175,
        fit: null
      },
      result: null,
      lastFocus: null
    };

    bindModalEvents(state);
    return state;
  }

  function bindModalEvents(state) {
    const modal = state.modal;

    modal.querySelectorAll('[data-fq-answer]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const key = btn.dataset.fqAnswer;
        const val = btn.dataset.fqValue;
        state.answers[key] = val;
        advanceFromAnswer(state, key);
      });
    });

    const slider = modal.querySelector('[data-fq-slider]');
    const metricVal = modal.querySelector('[data-fq-metric-value]');
    const imperial = modal.querySelector('[data-fq-imperial]');
    if (slider) {
      function syncHeight() {
        const cm = parseInt(slider.value, 10) || 175;
        const min = parseInt(slider.min, 10) || 140;
        const max = parseInt(slider.max, 10) || 220;
        const pct = ((cm - min) / (max - min)) * 100;
        state.answers.heightCm = cm;
        if (metricVal) metricVal.textContent = cm;
        if (imperial) imperial.textContent = cmToImperial(cm);
        slider.style.setProperty('--fq-progress', pct + '%');
        slider.setAttribute('aria-valuenow', String(cm));
      }
      slider.addEventListener('input', syncHeight);
      syncHeight();
    }

    const heightNext = modal.querySelector('[data-fq-height-next]');
    if (heightNext) {
      heightNext.addEventListener('click', function () {
        goToStep(state, 7);
      });
    }

    const backBtn = modal.querySelector('[data-fq-back]');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        goBack(state);
      });
    }

    const closeBtn = modal.querySelector('[data-fq-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeModal(state);
      });
    }

    state.overlay.addEventListener('click', function () {
      closeModal(state);
    });

    document.addEventListener('keydown', function (e) {
      if (!state.modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        closeModal(state);
      } else if (e.key === 'Tab') {
        trapFocus(state, e);
      }
    });

    const addBtn = modal.querySelector('[data-fq-add-to-cart]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        selectSizeAndClose(state);
      });
    }

    const restartBtn = modal.querySelector('[data-fq-restart]');
    if (restartBtn) {
      restartBtn.addEventListener('click', function () {
        restartQuiz(state);
      });
    }

    const helpBtn = modal.querySelector('[data-fq-help]');
    const helpPopover = modal.querySelector('[data-fq-help-popover]');
    if (helpBtn && helpPopover) {
      helpBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        const isOpen = !helpPopover.hidden;
        helpPopover.hidden = isOpen;
        helpBtn.setAttribute('aria-expanded', String(!isOpen));
      });
      modal.addEventListener('click', function (e) {
        if (helpPopover.hidden) return;
        if (!helpPopover.contains(e.target) && e.target !== helpBtn) {
          helpPopover.hidden = true;
          helpBtn.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  function advanceFromAnswer(state, key) {
    const map = {
      gender: 2,
      currentSize: 3,
      consistency: 4,
      belly: 5,
      body: 6,
      fit: 'result'
    };
    const next = map[key];
    if (next !== undefined) goToStep(state, next);
  }

  /* ---------- Step navigation ---------- */
  function goToStep(state, step) {
    if (step === 'result') {
      computeAndRenderResult(state);
    }

    state.modal.querySelectorAll('[data-fq-step]').forEach(function (el) {
      el.classList.remove('is-active');
    });
    const target = state.modal.querySelector('[data-fq-step="' + step + '"]');
    if (target) target.classList.add('is-active');

    state.currentStep = step;
    updateChrome(state);
    autofocusFirstInteractive(target);
  }

  function goBack(state) {
    if (state.currentStep === 1) return;
    if (state.currentStep === 'result') {
      goToStep(state, 7);
      return;
    }
    const current = parseInt(state.currentStep, 10);
    if (current > 1) goToStep(state, current - 1);
  }

  function updateChrome(state) {
    const indicator = state.modal.querySelector('[data-fq-step-indicator]');
    const back = state.modal.querySelector('[data-fq-back]');
    const subtitle = state.modal.querySelector('[data-fq-subtitle]');

    if (state.currentStep === 'result') {
      if (indicator) indicator.textContent = '';
      if (back) back.hidden = false;
      if (subtitle) subtitle.style.display = 'none';
    } else {
      const total = 7;
      if (indicator) indicator.textContent = state.currentStep + '/' + total;
      if (back) back.hidden = state.currentStep === 1;
      if (subtitle) subtitle.style.display = '';
    }
  }

  function autofocusFirstInteractive(stepEl) {
    if (!stepEl) return;
    const focusable = stepEl.querySelector('button, [tabindex]:not([tabindex="-1"]), input');
    if (focusable) {
      setTimeout(function () { focusable.focus(); }, 30);
    }
  }

  /* ---------- Size calculation ---------- */
  function calculateSize(answers) {
    let score = SIZES.indexOf(answers.currentSize);
    if (score < 0) score = SIZES.indexOf('M');

    if (answers.consistency === 'smaller') score -= 0.3;
    if (answers.consistency === 'larger')  score += 0.3;

    if (answers.belly === 'flat')  score -= 0.2;
    if (answers.belly === 'round') score += 0.4;

    if (answers.body === 'slim')   score -= 0.3;
    if (answers.body === 'curvy')  score += 0.4;

    if (answers.heightCm < 165)    score -= 0.2;
    if (answers.heightCm > 185)    score += 0.2;

    if (answers.fit === 'tight')   score -= 0.5;
    if (answers.fit === 'loose')   score += 0.5;

    const finalIndex = Math.min(Math.max(Math.round(score), 0), SIZES.length - 1);
    return SIZES[finalIndex];
  }

  /* ---------- Result render ---------- */
  function computeAndRenderResult(state) {
    const recommended = calculateSize(state.answers);
    state.result = { size: recommended };

    const resolved = resolveVariant(state.data, recommended);
    state.result.variant = resolved.variant;
    state.result.fallback = resolved.fallback;
    state.result.displaySize = resolved.displaySize;

    const sizeEl = state.modal.querySelector('[data-fq-result-size]');
    const warnEl = state.modal.querySelector('[data-fq-result-warning]');

    if (sizeEl) sizeEl.textContent = resolved.displaySize;

    if (warnEl) {
      if (resolved.fallback) {
        warnEl.innerHTML = 'Deine empfohlene Größe <strong>' + escapeHtml(recommended) + '</strong> ist leider nicht verfügbar. Wir empfehlen <strong>' + escapeHtml(resolved.displaySize) + '</strong>.';
        warnEl.hidden = false;
      } else if (!resolved.variant) {
        warnEl.innerHTML = 'Deine empfohlene Größe <strong>' + escapeHtml(recommended) + '</strong> ist aktuell ausverkauft.';
        warnEl.hidden = false;
      } else {
        warnEl.hidden = true;
        warnEl.textContent = '';
      }
    }

    const addBtn = state.modal.querySelector('[data-fq-add-to-cart]');
    if (addBtn) {
      addBtn.disabled = !resolved.variant;
    }

    writeCache(state.data.productId, resolved.displaySize);

    const hint = state.root.querySelector('[data-fq-cached-hint]');
    if (hint) {
      hint.innerHTML = ' · empfohlen: <strong>' + escapeHtml(resolved.displaySize) + '</strong>';
      hint.hidden = false;
    }
  }

  /* ---------- Variant lookup ---------- */
  function resolveVariant(data, recommendedSize) {
    const optKey = sizeOptionKey(data.sizeOptionIndex);
    const variants = data.variants || [];

    function findBySize(sz) {
      return variants.find(function (v) {
        const value = optKey ? v[optKey] : v.option1;
        return normalize(value) === normalize(sz) && v.available;
      });
    }

    let direct = findBySize(recommendedSize);
    if (direct) {
      return { variant: direct, fallback: false, displaySize: recommendedSize };
    }

    const recIdx = SIZES.indexOf(recommendedSize);
    if (recIdx >= 0) {
      for (let delta = 1; delta < SIZES.length; delta++) {
        const upIdx = recIdx + delta;
        if (upIdx < SIZES.length) {
          const v = findBySize(SIZES[upIdx]);
          if (v) return { variant: v, fallback: true, displaySize: SIZES[upIdx] };
        }
        const downIdx = recIdx - delta;
        if (downIdx >= 0) {
          const v = findBySize(SIZES[downIdx]);
          if (v) return { variant: v, fallback: true, displaySize: SIZES[downIdx] };
        }
      }
    }

    return { variant: null, fallback: false, displaySize: recommendedSize };
  }

  function sizeOptionKey(idx) {
    if (typeof idx !== 'number' || idx < 0) return 'option1';
    return 'option' + (idx + 1);
  }

  function normalize(s) {
    if (s == null) return '';
    return String(s).trim().toUpperCase();
  }

  /* ---------- Select size on product page + close ---------- */
  function selectSizeAndClose(state) {
    if (!state.result) return;
    const size = state.result.displaySize;
    const sizeOptionIdx = state.data.sizeOptionIndex;

    const selectors = [];
    if (sizeOptionIdx >= 0) {
      selectors.push('input[type="radio"][data-index="option' + (sizeOptionIdx + 1) + '"]');
    } else {
      selectors.push(
        'input[type="radio"][data-index="option1"]',
        'input[type="radio"][data-index="option2"]',
        'input[type="radio"][data-index="option3"]'
      );
    }

    let matched = null;
    for (let i = 0; i < selectors.length && !matched; i++) {
      const candidates = document.querySelectorAll(selectors[i]);
      for (let j = 0; j < candidates.length; j++) {
        const input = candidates[j];
        if (input.disabled) continue;
        if (normalize(input.value) === normalize(size)) {
          matched = input;
          break;
        }
      }
    }

    if (!matched) {
      const selects = document.querySelectorAll('select[name="id"], select[data-variant-input]');
      selects.forEach(function (sel) {
        if (matched) return;
        for (let i = 0; i < sel.options.length; i++) {
          const opt = sel.options[i];
          if (normalize(opt.text).indexOf(normalize(size)) !== -1 || normalize(opt.value).indexOf(normalize(size)) !== -1) {
            sel.value = opt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            matched = sel;
            break;
          }
        }
      });
    } else {
      if (!matched.checked) {
        matched.checked = true;
        const label = document.querySelector('label[for="' + matched.id + '"]');
        if (label) label.click();
        matched.dispatchEvent(new Event('change', { bubbles: true }));
        matched.dispatchEvent(new Event('input', { bubbles: true }));
      }
      try {
        matched.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) { /* noop */ }
    }

    closeModal(state);
  }

  function restartQuiz(state) {
    state.answers = {
      gender: null,
      currentSize: null,
      consistency: null,
      belly: null,
      body: null,
      heightCm: 175,
      fit: null
    };
    state.result = null;
    const slider = state.modal.querySelector('[data-fq-slider]');
    if (slider) slider.value = '175';
    const warnEl = state.modal.querySelector('[data-fq-result-warning]');
    if (warnEl) { warnEl.hidden = true; warnEl.textContent = ''; }
    goToStep(state, 1);
  }

  /* ---------- Open / close ---------- */
  function openModal(state) {
    state.lastFocus = document.activeElement;
    state.overlay.classList.add('is-open');
    state.modal.classList.add('is-open');
    document.body.classList.add('fq-no-scroll');
    goToStep(state, 1);
  }

  function closeModal(state) {
    state.overlay.classList.remove('is-open');
    state.modal.classList.remove('is-open');
    document.body.classList.remove('fq-no-scroll');
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      state.lastFocus.focus();
    }
  }

  /* ---------- Focus trap ---------- */
  function trapFocus(state, e) {
    const focusableSelector = 'button:not([disabled]):not([hidden]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = Array.from(state.modal.querySelectorAll(focusableSelector))
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---------- Helpers ---------- */
  function cmToImperial(cm) {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches - feet * 12);
    return feet + ' ft ' + inches + ' in';
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ---------- Boot ---------- */
  function boot() {
    document.querySelectorAll('[data-fq-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    const roots = e.target.querySelectorAll ? e.target.querySelectorAll('[data-fq-root]') : [];
    roots.forEach(initRoot);
  });
})();
